/**
 * Which project this conversation is in, on the conversation screen.
 *
 * Frank's glass test of 27 July failed here. Everything worked and none of it
 * was reachable on a phone: the chat screen never named the project, and the
 * only way to move a chat into one was in the drawer, under a section that
 * starts collapsed, behind a 24 point button. "Built but invisible" was the
 * verdict, and it was right.
 *
 * So: always present, the name is the way to the project, and every other
 * action sits behind one tap on a button a thumb can actually hit. Written
 * with a plain panel rather than the shared dropdown, because this one has to
 * be certain on a touch screen and a menu that needs a pointer is how we got
 * here.
 */

import { useEffect, useRef, useState } from "react";
import { Folder, FolderOpen, MoreHorizontal, X } from "lucide-react";
import { cn } from "../lib/utils";
import { useTaal } from "@/contexts/TaalContext";

export default function ProjectBadge({ projects = [], projectId = null, onOpenProject, onMoveToProject }) {
  const { t } = useTaal();
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  const project = projects.find((p) => p.id === projectId) || null;
  const elsewhere = projects.filter((p) => p.id !== projectId);
  // A menu with nothing in it is worse than no menu. There is something to
  // offer as soon as there is another project to move to, or a project to
  // leave.
  const hasMenu = elsewhere.length > 0 || Boolean(project);

  useEffect(() => {
    if (!open) return undefined;
    const away = (event) => {
      if (wrap.current && !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("touchstart", away);
    };
  }, [open]);

  const pick = (id) => {
    setOpen(false);
    onMoveToProject?.(id);
  };

  return (
    // EEN ONDERGRENS VAN TWEE TIKVLAKKEN (GL198-U, 24 oktober 2026): met `min-w-0` alleen kromp
    // dit blok op 390 punten tot onder zijn knoppen, en lag het menuknopje over de klok en het
    // Universum heen (gemeten: tikvlak 168 tot 212 over de Universumknop van 181 tot 225).
    <div className="relative flex min-w-[92px] md:min-w-0 shrink items-center gap-1" ref={wrap}>
      <button
        type="button"
        data-testid="chat-project-badge"
        // With a project the badge is a breadcrumb. Without one there is
        // nowhere to go, so it does the next most useful thing and opens the
        // way to put this conversation somewhere.
        onClick={() => (project ? onOpenProject?.(project.id) : setOpen((v) => !v))}
        className={cn(
          "tap-target flex min-w-0 items-center gap-1.5 px-2 py-1 rounded-full border text-xs max-w-[55vw] sm:max-w-none",
          project ? "border-transparent bg-muted" : "border-dashed text-muted-foreground"
        )}
      >
        {project ? (
          <>
            <Folder className="h-3.5 w-3.5 flex-shrink-0" style={{ color: project.color }} />
            <span className="truncate">{project.name}</span>
          </>
        ) : (
          <>
            <Folder className="h-3.5 w-3.5 flex-shrink-0" />
            {/* Op een smal scherm alleen het mapje: afgekapt bleef er "G" over
                (gemeten op 390 punten na de verversknop, 11 okt 2026). */}
            <span className="truncate hidden sm:inline">{t("Geen project")}</span>
            <span className="sr-only sm:hidden">{t("Geen project")}</span>
          </>
        )}
      </button>

      {hasMenu && (
        <button
          type="button"
          data-testid="chat-project-menu"
          aria-label={t("Projectopties voor dit gesprek")}
          onClick={() => setOpen((v) => !v)}
          className="tap-target flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-project-panel"
          className="absolute top-full left-0 z-30 mt-1 w-64 max-w-[85vw] rounded-xl border bg-popover shadow-lg overflow-hidden"
        >
          {elsewhere.length > 0 && (
            <>
              <p className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground uppercase">{t("Naar project")}</p>
              <div className="max-h-56 overflow-y-auto">
                {elsewhere.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`chat-project-move-${p.id}`}
                    onClick={() => pick(p.id)}
                    className="w-full flex items-center gap-2 px-3 py-3 text-sm text-left hover:bg-muted"
                  >
                    <Folder className="h-4 w-4 flex-shrink-0" style={{ color: p.color }} />
                    <span className="truncate">{p.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {project && (
            <>
              <button
                type="button"
                data-testid="chat-project-open"
                onClick={() => { setOpen(false); onOpenProject?.(project.id); }}
                className="w-full flex items-center gap-2 px-3 py-3 text-sm text-left border-t hover:bg-muted"
              >
                <FolderOpen className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t("{naam} openen", { naam: project.name })}</span>
              </button>
              <button
                type="button"
                data-testid="chat-project-remove"
                onClick={() => pick(null)}
                className="w-full flex items-center gap-2 px-3 py-3 text-sm text-left border-t hover:bg-muted"
              >
                <X className="h-4 w-4 flex-shrink-0" />
                <span>{t("Uit project halen")}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
